import { type AlmanacCableSubscription } from 'providers/CableConsumer/types'
import { useEffect, useRef } from 'react'
import useIsMounted from 'ismounted'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { Cancelable } from 'beautiful-react-hooks'
import { TreeFilesView, RefreshPayload } from '../FinderContextTypes'

const useItemsRealtime = ({
  folderId,
  onItemsMessage,
}: {
  folderId?: string
  onItemsMessage: ((payload: RefreshPayload) => void) & Cancelable
}) => {
  const isMounted = useIsMounted()
  const websocketConnection = useWebsocketConnection()
  const itemsSubscription = useRef<AlmanacCableSubscription>()

  useEffect(() => {
    if (!websocketConnection || !folderId || !isMounted.current) return

    itemsSubscription.current = websocketConnection.subscriptions.create(
      { channel: 'FilesViewSchemas::ItemsChannel', folder_id: folderId },
      { received: onItemsMessage },
    )

    return () => {
      itemsSubscription?.current?.unsubscribe()
    }
  }, [onItemsMessage, folderId, isMounted, websocketConnection])
}

const useFileViewUpdateRealtime = ({
  filesView,
  onUpdateMessage,
}: {
  filesView?: TreeFilesView
  onUpdateMessage: ((payload: RefreshPayload) => void) & Cancelable
}) => {
  const isMounted = useIsMounted()
  const websocketConnection = useWebsocketConnection()
  const viewUpdateSubscription = useRef<AlmanacCableSubscription>()
  const schemaId = filesView?.schema.id

  useEffect(() => {
    if (!websocketConnection || !schemaId || !isMounted.current) return

    viewUpdateSubscription.current = websocketConnection.subscriptions.create(
      { channel: 'FilesViewSchemas::UpdateChannel', files_view_schema_id: schemaId },
      { received: onUpdateMessage },
    )

    return () => {
      viewUpdateSubscription?.current?.unsubscribe()
    }
  }, [onUpdateMessage, schemaId, isMounted, websocketConnection])
}

const useViewsChannelRealtime = ({
  folderId,
  onViewsMessage,
}: {
  folderId?: string
  onViewsMessage: ((payload: RefreshPayload) => void) & Cancelable
}) => {
  const isMounted = useIsMounted()
  const websocketConnection = useWebsocketConnection()
  const viewsSubscription = useRef<AlmanacCableSubscription>()

  useEffect(() => {
    if (!websocketConnection || !folderId || !isMounted.current) return

    viewsSubscription.current = websocketConnection.subscriptions.create(
      { channel: 'FilesViewSchemas::ViewsChannel', folder_id: folderId },
      { received: onViewsMessage },
    )

    return () => {
      viewsSubscription?.current?.unsubscribe()
    }
  }, [onViewsMessage, folderId, isMounted, websocketConnection])
}

export { useFileViewUpdateRealtime, useItemsRealtime, useViewsChannelRealtime }
